import type { CSSProperties, FC } from "react"
import React, { useCallback, useEffect, useRef, useState } from "react"

import useResizeObserver from "@react-hook/resize-observer"
import type { DraggableData, DraggableEvent } from "react-draggable"
import { DraggableCore } from "react-draggable"
import styled from "styled-components"

import type { RefineDraggableEvent } from "~/utils/refineDraggableEvent"
import { refineDraggableEvent } from "~/utils/refineDraggableEvent"

export const useSize = (
  target: React.RefObject<HTMLDivElement>,
): DOMRect | null => {
  const [size, setSize] = React.useState<DOMRect | null>(null)

  React.useLayoutEffect(() => {
    if (!target.current) return
    setSize(target.current.getBoundingClientRect())
  }, [target])

  useResizeObserver(target, (entry) => setSize(entry?.contentRect))

  return size
}

const BG = `#ffffff`

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  transition: background-color 0.2s ease-in-out;
  position: relative;
  * {
    margin: 0;
    padding: 0;
  }
`
const DialContainer = styled.div`
  width: 70%;
  height: 70%;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 2;
`
const Gradient = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  position: absolute;
  z-index: 3;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 40%, ${BG} 75%);
  pointer-events: none;
  @media print {
    background: none;
  }
`
const ChildWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
  > div {
    width: 70%;
    height: 70%;
    justify-content: center;
    align-items: center;
  }
`
const HandleContainer = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  transition: transform 0.15s;
`
const Handle = styled.div<{ pos: number }>`
  width: 7.5%;
  height: 7.5%;
  min-width: 8px;
  min-height: 8px;
  border-radius: 50%;
  background: #0084ff;
  transition: box-shadow 0.5s;
  cursor: pointer;
  position: absolute;
  box-shadow: 0 0 15px 0px #0084ff7d;
  transform: translateY(${({ pos }) => pos}px);
`
const StyledDial = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  z-index: -1;
  cursor: pointer;
  border-radius: 50%;
  background: #f1f2f2;
  border: 2px solid white;
  box-shadow:
    6px 9px 40px 0px #afafafb2,
    -6px -6px 16px 0px #ffffff99;
`
const Outer = styled.div<{ isDragging: boolean }>`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  background-color: ${BG};
  display: flex;
  justify-content: center;
  align-items: center;
  background: #e7f3fa;
  border: 10px solid white;
`
const SubTextWrapper = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 150;
  display: flex;
  align-items: flex-end;
  pointer-events: none;
`
const toDeg = (rad: number) => {
  return rad * (180 / Math.PI)
}

const getDegFromQuadrant = (
  point: { x: number; y: number },
  deg: number,
  lastQuadrant: number,
  setLastQuadrant: React.Dispatch<React.SetStateAction<number>>,
): number => {
  const q1 = point.x >= 0 && point.y >= 0
  const q2 = point.x <= 0 && point.y >= 0
  const q3 = point.x <= 0 && point.y <= 0
  const q4 = point.x >= 0 && point.y <= 0
  if (q1 || q4) {
    if (lastQuadrant === 2) {
      return -90 - deg
    }
    setLastQuadrant(q1 ? 1 : 4)

    return 270 - deg
  }
  if (q2 || q3) {
    if (lastQuadrant === 1) {
      return 450 + deg
    }
    setLastQuadrant(q2 ? 2 : 3)

    return 90 + deg
  }

  return 0
}

const getNewPoint = ({ event, type }: ReturnType<RefineDraggableEvent>) => {
  switch (type) {
    case `mouse`:
      return {
        x: event.clientX,
        y: event.clientY,
      }
    case `touch`:
      return {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY,
      }
  }
}

export type DialProps = {
  max?: number
  min?: number
  increment?: number
  initial?: number
  showNotches?: boolean
  realisticDrag?: boolean
  debugRealisticDrag?: boolean
  children?: JSX.Element
  degrees: number
  setDegrees: (degrees: number) => void
  showProgress?: boolean
  dialStyle?: CSSProperties
  handleStyle?: CSSProperties
  mustUseHandle: boolean
  SubText?: JSX.Element
  onReady?: () => void
}

export const Dial: FC<DialProps> = ({
  max = 360,
  min = 0,
  increment = 45,
  showNotches = true,
  realisticDrag = false,
  degrees = 0,
  setDegrees,
  children,
  showProgress = true,
  dialStyle,
  handleStyle,
  debugRealisticDrag = false,
  mustUseHandle = false,
  SubText,
  onReady,
}) => {
  const [handlePos, setHandlePos] = useState(0)
  const diff = useRef(0)
  const handleRef = useRef<HTMLDivElement>(null)
  const dialRef = useRef<HTMLDivElement>(null)
  const size = useSize(dialRef)
  const [isDragging, setIsDragging] = useState(false)
  const [debugCoords, setDebugCoords] = useState({ x: 0, y: 0 })
  const [lastQuadrant, setLastQuadrant] = useState(2)

  const turnDialRealistic = useCallback(
    (e: DraggableEvent) => {
      if (!dialRef.current) return
      // get dimensions of dial and users mouse/touch position
      const userPoint = getNewPoint(refineDraggableEvent(e))
      const box = dialRef.current.getBoundingClientRect()
      const centerPoint = {
        y: box.top + box.height / 2,
        x: box.left + box.width / 2,
      }
      const point = {
        x: centerPoint.x - userPoint.x,
        y: centerPoint.y - userPoint.y,
      }

      // get the angle of the users mouse/touch position
      const sin =
        point.y / Math.sqrt(Math.pow(point.x, 2) + Math.pow(point.y, 2))
      const cos =
        point.x / Math.sqrt(Math.pow(point.x, 2) + Math.pow(point.y, 2))
      const theta = Math.asin(sin)
      const degFromQuad = getDegFromQuadrant(
        { x: -cos, y: -sin },
        toDeg(theta),
        lastQuadrant,
        setLastQuadrant,
      )

      // update debug coords if debugRealisticDrag is true
      debugRealisticDrag &&
        setDebugCoords({
          x: -cos * (box.height / 2),
          y: -sin * (box.height / 2),
        })

      // set the dial to the nearest increment
      const degAdd = degrees + increment
      const degSub = degrees - increment
      const newDeg =
        degFromQuad >= degAdd ? degAdd : degFromQuad <= degSub ? degSub : degrees
      setDegrees(newDeg > max ? max : newDeg < min ? min : newDeg)
    },
    [degrees, lastQuadrant, debugRealisticDrag, increment, max, min, setDegrees],
  )

  const turnDial = (f: DraggableEvent, d: DraggableData) => {
    diff.current = diff.current + d.deltaX + -d.deltaY
    const increase = increment * Math.floor(Math.abs(diff.current) / increment)
    if (diff.current >= increment) {
      diff.current = 0
      setDegrees(degrees + increase <= max ? degrees + increase : degrees)

      return
    }
    if (diff.current <= -increment) {
      diff.current = 0
      setDegrees(degrees - increase >= min ? degrees - increase : degrees)

      return
    }
  }

  useEffect(() => {
    const h = handleRef.current
    if (!h || !size) return
    setHandlePos(size.width / 2 - h.offsetHeight)
  }, [size, showProgress])

  // Notify parent when dial is ready (size available and handle position set)
  useEffect(() => {
    if (size && handlePos > 0 && onReady) {
      onReady()
    }
  }, [size, handlePos, onReady])

  return (
    <Wrapper>
      <Outer isDragging={isDragging}>
        {showNotches && (
          <>
            <svg
              viewBox="0 0 200 200"
              style={{
                position: `absolute`,
                width: `80%`,
                height: `80%`,
                borderRadius: `50%`,
              }}
            >
              <circle
                cx="50%"
                cy="50%"
                r="50%"
                transform="rotate(90 100 100)"
                fill="none"
                stroke="var(--blue)"
                strokeDasharray={`1, 18`}
                strokeWidth="7"
                opacity={0.5}
                strokeLinecap="butt"
              />
            </svg>
          </>
        )}
        <SubTextWrapper>{SubText && SubText}</SubTextWrapper>
        <ChildWrapper>
          <div>{children}</div>
        </ChildWrapper>
        <Gradient />
        <DialContainer
          style={{
            cursor: mustUseHandle ? `initial` : `pointer`,
          }}
        >
          <HandleContainer
            style={{
              transform: `rotate(${degrees}deg)`,
              pointerEvents: mustUseHandle ? `all` : `none`,
            }}
          >
            <DraggableCore
              onStart={() => setIsDragging(true)}
              onStop={() => setIsDragging(false)}
              onDrag={realisticDrag ? turnDialRealistic : turnDial}
              nodeRef={handleRef}
            >
              <Handle style={handleStyle} ref={handleRef} pos={handlePos} />
            </DraggableCore>
          </HandleContainer>
          {debugRealisticDrag && realisticDrag && (
            <div
              style={{
                position: `absolute`,
                transform: `translate(${debugCoords.x}px, ${debugCoords.y}px)`,
                background: `red`,
                zIndex: 20,
                width: `15px`,
                height: `15px`,
              }}
            />
          )}
          <DraggableCore
            onStart={() => setIsDragging(true)}
            onStop={() => setIsDragging(false)}
            onDrag={realisticDrag ? turnDialRealistic : turnDial}
            nodeRef={dialRef}
          >
            <StyledDial ref={dialRef} style={dialStyle} />
          </DraggableCore>
        </DialContainer>
      </Outer>
    </Wrapper>
  )
}
